import { useEffect, useRef } from 'react';

import { cn } from '@/lib/cn';

interface Props {
  /** H.264 RGB video URL (color channels). */
  rgbUrl: string;
  /** H.264 grayscale video URL (alpha mask — white=opaque, black=transparent). */
  alphaUrl: string;
  /** Poster for both videos while buffering. */
  poster?: string;
  /**
   * Known backdrop color the RGB stream was composited over (e.g. '#FF00FF'
   * for clips that went through the chroma-key magenta preprocessing).
   * When set, the per-pixel decomposite formula
   *   subject = (composite − bg × (1 − α)) / α
   * is applied on semi-transparent edge pixels so the border doesn't
   * carry the backdrop tint. Omit (or undefined) when the source backdrop
   * is unknown or arbitrary — then we fall back to per-pixel despill.
   */
  knownBgHex?: string;
  className?: string;
}

/**
 * Phase 7c — dual-stream alpha compositor.
 *
 * Some browsers (notably iOS WebKit) don't decode WebM VP9 alpha. The
 * universal fallback is to ship the matte as TWO H.264 videos:
 *   1. RGB stream    — color, no alpha (every browser decodes H.264).
 *   2. Alpha stream  — grayscale; white pixel = opaque, black = transparent.
 *
 * We play them side-by-side (offscreen), then composite per frame on a
 * single canvas: read R,G,B from the RGB stream and use the alpha stream's
 * red channel as the per-pixel alpha. ~5 ms/frame on M-class mobile.
 *
 * Two videos must stay in sync — we drive the alpha video off the RGB
 * video's timeupdate so any drift gets corrected. Both are muted/looped/
 * autoplay/playsInline.
 */
export function DualStreamVideo({ rgbUrl, alphaUrl, poster, knownBgHex, className }: Props) {
  const rgbRef = useRef<HTMLVideoElement | null>(null);
  const alphaRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const rgb = rgbRef.current;
    const alpha = alphaRef.current;
    const canvas = canvasRef.current;
    if (!rgb || !alpha || !canvas) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    let rafId = 0;
    let frameW = 0;
    let frameH = 0;
    let alphaScratch: HTMLCanvasElement | null = null;
    let alphaScratchCtx: CanvasRenderingContext2D | null = null;

    // Decompose backdrop tint on edge pixels when we know the bg color.
    const bg = knownBgHex ? hexToRgb(knownBgHex) : null;

    const onTimeUpdate = () => {
      // Keep alpha track in lock-step with RGB. Threshold to avoid endless
      // micro-adjustments.
      if (Math.abs(alpha.currentTime - rgb.currentTime) > 0.05) {
        alpha.currentTime = rgb.currentTime;
      }
    };
    rgb.addEventListener('timeupdate', onTimeUpdate);

    const draw = () => {
      if (rgb.readyState >= 2 && alpha.readyState >= 2 && !rgb.paused && !rgb.ended) {
        const W = rgb.videoWidth;
        const H = rgb.videoHeight;
        if (frameW !== W || frameH !== H) {
          frameW = W;
          frameH = H;
          canvas.width = W;
          canvas.height = H;
          alphaScratch = document.createElement('canvas');
          alphaScratch.width = W;
          alphaScratch.height = H;
          alphaScratchCtx = alphaScratch.getContext('2d', { willReadFrequently: true });
        }
        if (!alphaScratch || !alphaScratchCtx) return;

        // Draw RGB → main canvas.
        ctx.drawImage(rgb, 0, 0, frameW, frameH);
        const rgbData = ctx.getImageData(0, 0, frameW, frameH);

        // Draw alpha stream → scratch canvas, read pixel data.
        alphaScratchCtx.drawImage(alpha, 0, 0, frameW, frameH);
        const alphaData = alphaScratchCtx.getImageData(0, 0, frameW, frameH);

        // Combine: rgbData[a] = alphaData[r]. The alpha stream is grayscale
        // so R = G = B = perceived brightness; using R is fine and avoids
        // computing luminance.
        //
        // Decomposite on edges (when knownBgHex is set): for pixels with
        // partial alpha, the RGB stream is the alpha-blend of subject and
        // backdrop. Inverting the blend recovers the subject's true color
        // and removes the backdrop tint that otherwise shows up as a
        // colored halo around the silhouette:
        //   composite = subject·α + bg·(1−α)
        //   ⇒ subject = (composite − bg·(1−α)) / α
        const rgbPx = rgbData.data;
        const alphaPx = alphaData.data;
        if (bg) {
          for (let i = 0; i < rgbPx.length; i += 4) {
            const a = alphaPx[i];
            rgbPx[i + 3] = a;
            // Only decomposite on edge pixels — interior pixels (a ≈ 255)
            // are already the pure subject. Fully transparent pixels
            // (a = 0) get skipped because the formula divides by α.
            if (a > 4 && a < 250) {
              const an = a / 255;
              const inv = 1 - an;
              const r = (rgbPx[i] - bg.r * inv) / an;
              const g = (rgbPx[i + 1] - bg.g * inv) / an;
              const b = (rgbPx[i + 2] - bg.b * inv) / an;
              rgbPx[i]     = r < 0 ? 0 : r > 255 ? 255 : r;
              rgbPx[i + 1] = g < 0 ? 0 : g > 255 ? 255 : g;
              rgbPx[i + 2] = b < 0 ? 0 : b > 255 ? 255 : b;
            }
          }
        } else {
          for (let i = 0; i < rgbPx.length; i += 4) {
            rgbPx[i + 3] = alphaPx[i];
          }
        }
        ctx.putImageData(rgbData, 0, 0);
      }
      rafId = requestAnimationFrame(draw);
    };

    rafId = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(rafId);
      rgb.removeEventListener('timeupdate', onTimeUpdate);
    };
  }, [knownBgHex]);

  return (
    <>
      <video
        ref={rgbRef}
        src={rgbUrl}
        poster={poster}
        autoPlay
        loop
        muted
        playsInline
        crossOrigin="anonymous"
        className="hidden"
      />
      <video
        ref={alphaRef}
        src={alphaUrl}
        autoPlay
        loop
        muted
        playsInline
        crossOrigin="anonymous"
        className="hidden"
      />
      <canvas
        ref={canvasRef}
        className={cn('h-full w-full object-contain', className)}
      />
    </>
  );
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  const v = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(v, 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}
